import axios from 'axios';


const API_URL = '/public/';

class PublicService {

    /**
     * Fetches a module based on the provided base identifier.
     *
     * @param {string} base - The base identifier for the module.
     * @returns {Promise<Object|boolean>} - A promise that resolves to the module data if successful, or false if an error occurs or the base is invalid.
     */
    async getModule(base) {
        if (!base || base == '') return false;
        try {
            let res = await axios.get(API_URL + 'module/get.php?base=' + base);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async fullLoadModule(base) {
        if (!base || base == '') return false;
        try {
            let res = await axios.get(API_URL + 'module/load.php?base=' + base);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Fetches courses for a given module.
     *
     * @param {string} id_module - The ID of the module to fetch courses for.
     * @returns {Promise<Object|boolean>} - A promise that resolves to the course data if successful, or false if an error occurs or the id_module is invalid.
     */
    async getCourses(id_module) {
        if (!id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'courses/get.php?id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Fetches the packs and courses for a given module.
     *
     * @param {string} id_module - The ID of the module.
     * @returns {Promise<Object|boolean>} The data from the response if successful, otherwise false.
     */
    async getPacksCourses(id_module) {
        if (!id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'packs_courses/get.php?id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Fetches options for a given module ID.
     *
     * @param {string} id_module - The ID of the module to fetch options for.
     * @returns {Promise<Object|boolean>} A promise that resolves to the options data if successful, or false if an error occurs or the id_module is invalid.
     */
    async getOptions(id_module) {
        if (!id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'options/get.php?id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Fetches activities for a given module.
     *
     * @param {string} id_module - The ID of the module to fetch activities for.
     * @returns {Promise<Object|boolean>} - A promise that resolves to the activities data if successful, or false if an error occurs or the id_module is invalid.
     */
    async getActivites(id_module) {
        if (!id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'activites/get.php?id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async getOptionsForChampFormulaire(id, value) {
        if (!id || id == '') return false;
        if (!value) value = '';
        const params = {
            id: id,
            selected_value: value
        }
        try {
            let res = await axios.post(API_URL + 'champ_formulaire/get_options.php', params);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Fetches articles from the boutique based on the provided module ID.
     *
     * @param {string} id_module - The ID of the module to fetch articles for.
     * @returns {Promise<Object|boolean>} - A promise that resolves to the data of the articles if successful, or false if an error occurs or the id_module is invalid.
     */
    async getArticlesBoutique(id_module) {
        if (!id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'articles/get.php?id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Fetches metadata for a given resource type and resource ID.
     *
     * @param {string} resource_type - The type of the resource.
     * @param {string} id_resource - The ID of the resource.
     * @returns {Promise<Object|boolean>} - A promise that resolves to the metadata object if successful, or false if an error occurs or parameters are invalid.
     */
    async getMetadatas(resource_type, id_resource) {
        if (!resource_type || resource_type == '' || !id_resource || id_resource == '') return false;
        try {
            let res = await axios.get(API_URL + 'metadatas/get.php?resource_type=' + resource_type + '&id_resource=' + id_resource);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Fetches the VIP link for a given module.
     *
     * @param {string} id_module - The ID of the module.
     * @param {string} token - The authentication token.
     * @returns {Promise<Object|boolean>} The response data if successful, otherwise false.
     */
    async getLienVIP(id_module, token) {
        if (!id_module || id_module == '' || !token || token == '') return false;
        try {
            let res = await axios.get(API_URL + 'lien_vip/get.php?id_module=' + id_module + '&token=' + token);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async getLienVIPMultiUsage(id_module, token) {
        if (!id_module || id_module == '' || !token || token == '') return false;
        try {
            let res = await axios.get(API_URL + 'lien_vip_multi_usage/get.php?id_module=' + id_module + '&token=' + token);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }
    /**
     * Fetches form fields for a given module.
     *
     * @param {string} id_module - The ID of the module to fetch form fields for.
     * @returns {Promise<Object|boolean>} - A promise that resolves to the form fields data or false if an error occurs or the id_module is invalid.
     */
    async getChampsFormulaire(id_module) {
        if (!id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'champs_formulaire/get.php?id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async getCodeReduction(nom, id_module) {
        if (!nom || nom == '' || !id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'code_reduction/get.php?nom=' + nom + '&id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async removeCodeReductionUsage(id) {
        if (!id) return false;
        try {
            await axios.get(API_URL + 'code_reduction/remove_usage.php?id=' + id);
            return true;
        }
        catch (err) {
            return false;
        }
    }

    /**
     * Checks the availability of a booking.
     *
     * @param {Object} booking - The booking details to check availability for.
     * @returns {Promise<Object|boolean>} - Returns the availability data if successful, otherwise returns false.
     */
    async checkCommandeAvailabilities(booking) {
        if (!booking) return false;
        try {
            let res = await axios.post(API_URL + 'commande/check_availabilities.php', booking);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async checkIncoherence(nom, prenom, sexe) {
        try {
            let res = await axios.post(API_URL + 'incoherence/get.php', { nom: nom, prenom: prenom, sexe: sexe });
            return res.data;
        }
        catch (err) {
            return { "nom_valide": true, "prenom_valide": true, "sexe_valide": true };
        }
    }

    /**
     * Récupère les items disponibles dans la bourse aux dossards pour un module donné
     * 
     * @param {string} id_module - L'ID du module
     * @returns {Promise<Object|boolean>} - Les données de la bourse aux dossards ou false en cas d'erreur
     */
    async getBourseDossardsItems(id_module) {
        if (!id_module || id_module == '') return false;
        try {
            let res = await axios.get(API_URL + 'bourse_dossards/get.php?id_module=' + id_module);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

    async getDossardBourse(id_dossard_bourse) {
        if (!id_dossard_bourse || id_dossard_bourse == '') return false;
        try {
            let res = await axios.get(API_URL + 'bourse_dossard/get.php?id_dossard_bourse=' + id_dossard_bourse);
            return res.data;
        }
        catch (err) {
            return false;
        }
    }

}

export default new PublicService();